/**
 * ForecastAccuracyChart — Historical forecast accuracy and current month trajectory.
 *
 * Two sections:
 * 1. Stats cards: Avg Accuracy, Band Hit Rate, Avg Bias
 * 2. Historical accuracy bar chart (color-coded by threshold)
 * 3. Current month trajectory showing daily forecast convergence
 */

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  AreaChart,
  Area,
  Legend,
} from "recharts";
import { ForecastAccuracyData } from "../utils/PowerBIApiCall";
import {
  formatCurrencyAxis as formatYAxis,
  formatCurrency,
} from "../utils/format";
import { SERIES, GRID, AXIS_TICK } from "../utils/chartTheme";

interface ForecastAccuracyChartProps {
  data: ForecastAccuracyData;
}

// ─── Formatting ─────────────────────────────────────────────────────────────

function formatMonth(month: string): string {
  const [year, mo] = month.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[parseInt(mo, 10) - 1]} ${year}`;
}

function formatDate(isoDate: string): string {
  const d = new Date(isoDate);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function getAccuracyColor(pct: number): string {
  if (pct >= 97) return SERIES.positive; // green
  if (pct >= 93) return SERIES.warning; // amber
  return SERIES.negative; // red
}

// ─── Custom Tooltips ────────────────────────────────────────────────────────

function AccuracyTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    color: string;
    name: string;
    value: number;
    payload: Record<string, unknown>;
  }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-white border border-gray-200/70 rounded-xl shadow-pop p-3 max-w-xs">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">{label}</p>
      <div className="space-y-0.5 text-xs">
        <p>
          <span className="text-gray-500">Accuracy:</span>{" "}
          <span
            className="font-semibold tabular-nums"
            style={{ color: getAccuracyColor(data.accuracyPct as number) }}
          >
            {(data.accuracyPct as number).toFixed(1)}%
          </span>
        </p>
        <p>
          <span className="text-gray-500">Forecast:</span>{" "}
          <span className="tabular-nums">
            {formatCurrency(data.lastForecast as number)}
          </span>
        </p>
        <p>
          <span className="text-gray-500">Actual:</span>{" "}
          <span className="tabular-nums">
            {formatCurrency(data.actualTotal as number)}
          </span>
        </p>
        <p>
          <span className="text-gray-500">Band:</span>{" "}
          {(data.withinBand as boolean) ? (
            <span className="text-emerald-600">Within band</span>
          ) : (
            <span className="text-red-500">Outside band</span>
          )}
        </p>
      </div>
    </div>
  );
}

function TrajectoryTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200/70 rounded-xl shadow-pop p-3">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-xs" style={{ color: entry.color }}>
          <span className="font-medium">{entry.name}:</span>{" "}
          <span className="tabular-nums">{formatCurrency(entry.value)}</span>
        </p>
      ))}
    </div>
  );
}

// ─── Custom bar with accuracy-based coloring ────────────────────────────────

function AccuracyBar(props: Record<string, unknown>) {
  const { x, y, width, height, payload } = props as {
    x: number;
    y: number;
    width: number;
    height: number;
    payload: { accuracyPct: number };
  };
  const color = getAccuracyColor(payload.accuracyPct);
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={color}
      rx={3}
      ry={3}
    />
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ForecastAccuracyChart({ data }: ForecastAccuracyChartProps) {
  const { accuracyHistory, currentMonthTrajectory, stats } = data;

  if (accuracyHistory.length === 0 && currentMonthTrajectory.length === 0) {
    return null;
  }

  // Prepare accuracy chart data
  const accuracyChartData = accuracyHistory.map((r) => ({
    ...r,
    monthLabel: formatMonth(r.month),
  }));

  // Prepare trajectory chart data
  const trajectoryChartData = currentMonthTrajectory.map((s) => ({
    ...s,
    dateLabel: formatDate(s.date),
  }));

  const budgetFull = currentMonthTrajectory[0]?.budgetFull || 0;

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          Forecast Accuracy
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          How well past month-end forecasts predicted actuals
        </p>
      </div>

      {/* Stats Cards */}
      {stats.totalMonths > 0 && (
        <div className="grid grid-cols-3 gap-4 px-6 py-4 border-b border-gray-50">
          <div className="text-center">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Avg Accuracy
            </p>
            <p
              className="text-xl font-bold mt-0.5"
              style={{ color: getAccuracyColor(stats.avgAccuracyPct) }}
            >
              {stats.avgAccuracyPct.toFixed(1)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Band Hit Rate
            </p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">
              {stats.withinBandCount}/{stats.totalMonths}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Avg Bias
            </p>
            <p
              className="text-xl font-bold mt-0.5"
              style={{
                color:
                  Math.abs(stats.avgBiasPct) < 3
                    ? SERIES.positive
                    : SERIES.warning,
              }}
            >
              {stats.avgBiasPct > 0 ? "+" : ""}
              {stats.avgBiasPct.toFixed(1)}%
            </p>
          </div>
        </div>
      )}

      {/* Historical Accuracy Chart */}
      {accuracyChartData.length > 0 && (
        <div className="px-4 py-5 border-b border-gray-50">
          <p className="text-xs font-medium text-gray-500 px-2 mb-3">
            Monthly Forecast Accuracy
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart
              data={accuracyChartData}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid {...GRID} vertical={false} />
              <XAxis
                dataKey="monthLabel"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                domain={[80, 105]}
                tickFormatter={(v: number) => `${v}%`}
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                width={45}
              />
              <Tooltip content={<AccuracyTooltip />} />
              <ReferenceLine
                y={95}
                stroke={SERIES.warning}
                strokeDasharray="4 4"
                strokeWidth={1}
                label={{
                  value: "95%",
                  position: "right",
                  fontSize: 10,
                  fill: SERIES.warning,
                }}
              />
              <Bar
                dataKey="accuracyPct"
                name="Accuracy"
                maxBarSize={40}
                shape={<AccuracyBar />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Current Month Trajectory */}
      {trajectoryChartData.length > 1 && (
        <div className="px-4 py-5">
          <p className="text-xs font-medium text-gray-500 px-2 mb-3">
            Current Month — Forecast Trajectory
          </p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={trajectoryChartData}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <defs>
                <linearGradient id="forecastBand" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={SERIES.primary}
                    stopOpacity={0.15}
                  />
                  <stop
                    offset="95%"
                    stopColor={SERIES.primary}
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID} vertical={false} />
              <XAxis
                dataKey="dateLabel"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                width={65}
              />
              <Tooltip content={<TrajectoryTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
              />
              {/* High band */}
              <Area
                type="monotone"
                dataKey="forecastHigh"
                name="High"
                stroke="none"
                fill="url(#forecastBand)"
                fillOpacity={1}
              />
              {/* Low band */}
              <Area
                type="monotone"
                dataKey="forecastLow"
                name="Low"
                stroke="none"
                fill="#ffffff"
                fillOpacity={1}
              />
              {/* Forecast line */}
              <Area
                type="monotone"
                dataKey="forecastTotal"
                name="Forecast"
                stroke={SERIES.primary}
                strokeWidth={2}
                fill="none"
                dot={{ r: 3, fill: SERIES.primary }}
              />
              {/* Budget reference */}
              {budgetFull > 0 && (
                <ReferenceLine
                  y={budgetFull}
                  stroke={SERIES.budget}
                  strokeDasharray="6 3"
                  strokeWidth={1.5}
                  label={{
                    value: `Budget: ${formatYAxis(budgetFull)}`,
                    position: "right",
                    fontSize: 10,
                    fill: SERIES.budget,
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
